import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useRef } from 'react';
import { defineMessages } from 'react-intl';
import { useMixpanel } from '../../contexts/MixpanelContext';
import { useDynamicCallback } from '../../hooks/useDynamicCallback';
import { MixpanelEvent } from '../../tokens/mixpanel';
import { Button, IconButton } from '../Button';
import { Box, Flex } from '../Core';
import {
  CheckboxAutocompleteDropdown,
  FormControlSizes,
  Input,
  useDropdownPopper,
  useMultiSelectAutocomplete,
} from '../Form';
import { Icon, IconName } from '../Icons';
import { FormattedMessage } from '../Intl';
import { Text } from '../Text';
import { getAgGridColId } from './columns/getAgGridColId';

const messages = defineMessages({
  columns: {
    defaultMessage: 'Columns',
    id: 'BlotterTable.columns',
  },
  resetAll: {
    defaultMessage: 'Reset All',
    id: 'BlotterTable.resetAll',
  },
  selectAll: {
    defaultMessage: 'Select All',
    id: 'BlotterTable.selectAll',
  },
});

interface BlotterTableColumnSelectorProps {
  /**
   * Columns to update selection of.
   */
  columns: ColDef[];
  /**
   * Record of ColIds as keys and boolean to represent visibility of column, true=visible.
   */
  visibility: Record<string, boolean>;
  /**
   * Update function called whenever selection changes.
   */
  onChange: (visibility: Record<string, boolean>) => void;
  /**
   * If provided, takes precedence over columns hide property. Is used for initial selection as well as
   * resetting visibilities to default.
   */
  defaultVisibility?: Record<string, boolean>;
}

/**
 * A blotter table column selector that given a list of columns allow hiding and showing
 * columns.
 * Right now additional logic is needed to propagate visibility-changes to the blotter table,
 * specifically `setColumnsVisible` in a useEffect listening to the state of visibility.
 *
 *
 * TODO(robertz): This component would benefit from using usePersistedBlotterTable.
 */
export function BlotterTableColumnSelector({
  columns,
  onChange,
  visibility,
  defaultVisibility,
}: BlotterTableColumnSelectorProps) {
  const columnDefaultVisibility = useMemo(
    () =>
      defaultVisibility ??
      columns.reduce(
        (prev: Record<string, boolean>, colDef) => {
          const id = getAgGridColId(colDef);
          return {
            ...prev,
            [id]: colDef.hide ?? true,
          };
        },
        {} satisfies Record<string, boolean>
      ),
    [columns, defaultVisibility]
  );

  const allColumnsHidden: Record<string, boolean> = useMemo(
    () =>
      columns.reduce(
        (prev: Record<string, boolean>, colDef) => {
          const id = getAgGridColId(colDef);
          return {
            ...prev,
            [id]: false,
          };
        },
        {} satisfies Record<string, boolean>
      ),
    [columns]
  );

  const mixpanel = useMixpanel();

  const selectedItems = useMemo(() => {
    return columns.filter(col => col.field && visibility[col.field]);
  }, [visibility, columns]);

  const updateVisibility = useDynamicCallback((newSelections: ColDef[]) => {
    mixpanel.track(MixpanelEvent.UpdateColumns);
    const update = newSelections.reduce(
      (prev: Record<string, boolean>, colDef) => {
        if (!colDef.field) {
          return prev;
        }
        return { ...prev, [colDef.field]: true };
      },
      { ...allColumnsHidden }
    );
    onChange(update);
  });

  const propertyInputRef = useRef<HTMLInputElement>(null);

  const isItemDisabled = useCallback(
    (column: ColDef) => {
      // Prevent no selected columns, and only disable last one if length is 1.
      return selectedItems.length === 1 && !!selectedItems.find(c => c.field === column.field);
    },
    [selectedItems]
  );

  const getLabel = useDynamicCallback((colDef: ColDef) => {
    return colDef.headerName ?? colDef.field ?? colDef.colId ?? '';
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const { autocompleteOutput, multipleSelectionOutput } = useMultiSelectAutocomplete({
    selections: selectedItems,
    items: columns,
    getLabel,
    isItemDisabled,
    initialSortByLabel: true,
    onChange: updateVisibility,
    clearInputAfterSelection: false,
    removeItemOnInputBackspace: true,
    highlightInputTextAfterSelection: true,
    inputRef,
  });

  const { isOpen, closeMenu, openMenu, getInputProps, getMenuProps } = autocompleteOutput;
  const handleClickSettings = useDynamicCallback(() => {
    if (isOpen) {
      closeMenu();
    } else {
      mixpanel.track(MixpanelEvent.OpenOptionsColumnsSelector);
      openMenu();
    }
  });
  const columnsReset = useMemo(
    () =>
      columns.filter(colDef => {
        if (!colDef.field) {
          return false;
        }
        if (colDef.field in columnDefaultVisibility) {
          return columnDefaultVisibility[colDef.field];
        }
        return false;
      }),
    [columnDefaultVisibility, columns]
  );
  const areAllColumnsSelected = selectedItems.length === columns.length;
  const areColumnsResetEqualToAll = selectedItems.length === columnsReset.length;

  const handleSelectAllColumns = useDynamicCallback(() => {
    mixpanel.track(MixpanelEvent.SelectAllColumns);
    updateVisibility(columns);
  });
  const handleResetColumns = useDynamicCallback(() => {
    mixpanel.track(MixpanelEvent.ResetColumns);
    updateVisibility(columnsReset);
  });

  const { getDropdownProps } = multipleSelectionOutput;
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const dropdownPopper = useDropdownPopper({
    isOpen,
    referenceElement: dropdownRef.current,
    dropdownWidth: '240px',
    dropdownPlacement: 'bottom-end',
  });

  return (
    <div>
      <Box>
        <IconButton
          data-testid="column-selector-icon-button"
          icon={IconName.Cog}
          size={FormControlSizes.Small}
          ref={dropdownRef}
          onClick={handleClickSettings}
        />
      </Box>
      <div {...getMenuProps()}>
        <CheckboxAutocompleteDropdown
          {...autocompleteOutput}
          {...dropdownPopper}
          addSelectedItem={multipleSelectionOutput.addSelectedItem}
          removeSelectedItem={multipleSelectionOutput.removeSelectedItem}
          selectedItems={selectedItems}
          maxHeight={480}
          groupMaxHeight={480}
          childrenAboveResults={
            <Flex background="backgroundDropdownSelected" flexDirection="column">
              <Input
                {...getInputProps(getDropdownProps({ ref: propertyInputRef }))}
                data-testid="search-columns-input"
                placeholder="Search Columns"
              />
              <Flex
                py="spacing4"
                px="spacing12"
                color="gray.090"
                fontSize="fontSizeXs"
                gap="spacing2"
                alignItems="center"
              >
                <Icon icon={IconName.BookOpen} size="fontSizeSmall" />
                <Text textTransform="uppercase" mr="auto">
                  <FormattedMessage {...messages.columns} />
                </Text>
                {areAllColumnsSelected ? (
                  <Button
                    key="reset"
                    style={{ textTransform: 'uppercase' }}
                    size={FormControlSizes.Tiny}
                    ghost
                    onClick={handleResetColumns}
                    disabled={areColumnsResetEqualToAll}
                  >
                    <FormattedMessage {...messages.resetAll} />
                  </Button>
                ) : (
                  <Button
                    key="select"
                    style={{ textTransform: 'uppercase' }}
                    size={FormControlSizes.Tiny}
                    ghost
                    onClick={handleSelectAllColumns}
                    data-testid="select-all-columns-button"
                  >
                    <FormattedMessage {...messages.selectAll} />
                  </Button>
                )}
              </Flex>
            </Flex>
          }
        />
      </div>
    </div>
  );
}
